// ** React Imports
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

// ** MUI Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import InputLabel from "@mui/material/InputLabel";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CardContent from "@mui/material/CardContent";
import FormControl from "@mui/material/FormControl";
import OutlinedInput from "@mui/material/OutlinedInput";
import { styled } from "@mui/material/styles";
import MuiCard from "@mui/material/Card";
import InputAdornment from "@mui/material/InputAdornment";
import Footer from "../components/front-end/footer";
import FrontHeader from "../components/front-end/Header";

import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { signin } from "../redux/user/userActions";
import { checkSpace } from "../helper_functions/Functions";
import Error from "../components/error-msg";
import PositionedSnackbar from "../components/snackbar";
import SimpleLoader from "../components/loader";
import {
  fetchAllList,
  showLoader,
  showToast,
} from "../redux/common/commonActions";
import GoogleSignup from "./signup/GoogleSignup";


// ** Styled Components
const Card = styled(MuiCard)(({ theme }) => ({
  [theme.breakpoints.up("sm")]: { width: "28rem" },
}));

const LinkStyled = styled("a")(({ theme }) => ({
  fontSize: "0.875rem",
  textDecoration: "none",
  color: theme.palette.primary.main,
  cursor: "pointer",
}));

const style1 = {
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  height: "100%",
  p: { xl: "3rem", lg: "2rem", md: "2rem" },
  pb: "1rem",
  mt: { xl: "3rem", lg: "2rem", md: "2rem" },
};


const LoginPage = () => {
  // ** Hook
  const userData = useSelector((state) => state.userState);
  const commonState = useSelector((state) => state.commonState);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (userData.error !== "") {
      // setMsg("Incorrect username & password");
      // setShowMsg(true);
    } else {
      setMsg("");
      setShowMsg(false);
    }
  }, [userData]);

  // ** State
  const [showMsg, setShowMsg] = useState(false);
  const [msg, setMsg] = useState("");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState(0);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [apiResponse, setApiResponse] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleChangePassword = (event) => {
    // setValues({ ...values, [prop]: event.target.value })
    // setPassword(event.target.value)
    const val = event.target.value;
    if (!checkSpace(val) && val.length > 0) {
      setPassword(val);
      setPasswordError(false);
    } else {
      setPassword("");
      setPasswordError(true);
    }
  };

  const handleChangeEmail = (event) => {
    // setValues({ ...values, [prop]: event.target.value })
    // setPassword(event.target.value)
    const val = event.target.value;
    if (!checkSpace(val) && val.length > 0) {
      setEmail(val);
      setEmailError(false);
    } else {
      setEmail("");
      setEmailError(true);
    }
  };

  const handleClickShowPassword = () => {
    // setValues({ ...values, showPassword: !values.showPassword })
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const validateFields = (name) => {
    let status = false;

    if (name === "email") {
      if (email.toString().length > 0 && email?.includes(".") && email?.includes("@")) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const isEmailValid = emailRegex.test(email)
        setEmailError(!isEmailValid);
        status = !isEmailValid;
      } else {
        setEmailError(true);
        status = true;
      }
    }

    if (name === "password") {
      if (password.toString().length > 0) {
        setPasswordError(false);
        status = false;
      } else {
        setPasswordError(true);
        status = true;
      }
    }

    return !!status;
  };

  const formSubmit = () => {
    validateFields("email");
    validateFields("password");
    if (
      !emailError &&
      emailError !== 0 &&
      !passwordError &&
      passwordError !== 0
    ) {
      dispatch(showLoader({ show: true, msg: "Signing in..." }));
      setApiResponse(true);
      dispatch(signin(email, password, "user"));
      setTimeout(() => {
        if (userData?.isLoggedIn && window.location?.pathname === '/login') {
          window.location.reload()
        }
      }, 5000)
    } else {
      dispatch(showToast({ show: true, msg: "Issue in signing in..." }));
      setTimeout(() => {
        if (userData?.isLoggedIn && window.location?.pathname === '/login') {
          window.location.reload()
        }
      }, 100)
    }

  };

  const handleClose = () => {
    setShowMsg(false);
  };

  useEffect(() => {
    if (apiResponse && userData.error !== "") {
      setMsg("Incorrect username & password");
      setShowMsg(true);
      setIsLoading(false);
    } else {
      setMsg("");
      setShowMsg(false);
      setIsLoading(false);
    }
  }, [userData]);

  const handleFormOptions = () => {
    if (commonState?.countryData?.data?.length === 0 || !commonState?.countryData?.data) {
      dispatch(fetchAllList("fetchCountries"));
    }
    if (commonState?.serviceData?.data?.length === 0 || !commonState?.serviceData?.data) {
      dispatch(fetchAllList("services"));
    }
    if (commonState?.subjectData?.data?.length === 0 || !commonState?.subjectData?.data) {
      dispatch(fetchAllList("subjects"));
    }
    if (commonState?.academicLevelData?.data?.length === 0 || !commonState?.academicLevelData?.data) {
      dispatch(fetchAllList("academicLevel"));
    }
    if (commonState?.universityData?.data?.length === 0 || !commonState?.universityData?.data) {
      dispatch(fetchAllList("university"));
    }
    if (commonState?.wordCountData?.data?.length === 0 || !commonState?.wordCountData?.data) {
      dispatch(fetchAllList("wordCount"));
    }
    if (commonState?.referencingStyleData?.data?.length === 0 || !commonState?.referencingStyleData?.data) {
      dispatch(fetchAllList("referencingStyle"));
    }
    if (commonState?.softwareRequirementData?.data?.length === 0 || !commonState?.softwareRequirementData?.data) {
      dispatch(fetchAllList("softwareRequired"));
    }
    if (commonState?.cancellationReason?.data?.length === 0 || !commonState?.cancellationReason?.data) {
      dispatch(fetchAllList("cancellationReason"));
    }
  }

  useEffect(() => {
    handleFormOptions()
    dispatch(showLoader({ show: false, msg: "" }));
  }, []);

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: "100%",
      }}
    >
      {" "}
      <FrontHeader />
      <Box
        sx={{
          py: { sm: 2, xs: 2, md: 0, lg: 0 },
          display: "flex",
          flexDirection: "column",
          flex: 1,
          height: { xs: "auto", sm: "auto", md: "100%", lg: "100%" },
        }}
      >
        <Helmet>
          <title>Login - Acadlance</title>
          <meta name="description" content="Login - Acadlance" />
          <link rel="icon" href="/favicon-acadlance/favicon.ico" />
        </Helmet>
        <Box id="dd" className="content-center" sx={style1}>
          <Card
            id="cc"
            sx={{
              zIndex: 1,
              alignSelf: "center",
              boxShadow: "0px 2px 10px 0px rgba(58, 53, 65, 0.1)",
              borderRadius: "12px",
            }}
          >
            <CardContent sx={{ padding: 5 }}>
              <Box
                onClick={() => navigate("/")}
                sx={{
                  mb: 4,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: 'pointer',
                  flexDirection: 'column'
                }}
              >
                <img
                  src={"/logo.webp"}
                  alt="Logo"

                />
                <Typography
                  variant="span"
                  component="span"
                  sx={{
                    marginTop: '0.25rem',
                    color: '#6d4cb1',
                    fontSize: '0.75rem',
                    lineHeight: '0.75rem',
                    textAlign: 'center',
                  }}
                >
                  Pioneering Solution
                </Typography>
                {/* <Typography
                  variant="h6"
                  sx={{
                    ml: 1,
                    lineHeight: 1,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    fontSize: "1.5rem !important",
                    letterSpacing: "0.15px",
                    marginLeft: "0.75rem",
                    color: "#212B36",
                  }}
                >
                  {"Acadlance"}
                </Typography> */}
              </Box>
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: "1.5rem",
                    lineHeight: 1.334,
                    letterSpacing: 0,
                    color: "#212B36",
                    fontWeight: 600,
                    marginBottom: "0.375rem",
                  }}
                >
                  {/* Welcome back! 👋 */}
                  Welcome back!
                </Typography>
                <Typography variant="body2">
                  We are glad to see you again
                </Typography>
              </Box>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel htmlFor="auth-login-email">Email</InputLabel>
                <OutlinedInput
                  label="Email"
                  value={email}
                  id="auth-login-email"
                  onChange={handleChangeEmail}
                  type={"text"}
                  error={emailError === 0 ? false : emailError}
                />
                {emailError && emailError !== 0 ? (
                  <Error errorMsg={"please fill the email"} />
                ) : null}
              </FormControl>
              <FormControl fullWidth>
                <InputLabel htmlFor="auth-login-password">Password</InputLabel>
                <OutlinedInput
                  label="Password"
                  value={password}
                  id="auth-login-password"
                  onChange={handleChangePassword}
                  type={showPassword ? "text" : "password"}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        aria-label="toggle password visibility"
                      >
                        {showPassword ? (
                          <VisibilityIcon />
                        ) : (
                          <VisibilityOffIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  }
                  error={passwordError === 0 ? false : passwordError}
                />
                {passwordError && passwordError !== 0 ? (
                  <Error errorMsg={"please fill the password"} />
                ) : null}
              </FormControl>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-end",
                  flexWrap: "wrap",
                  justifyContent: "flex-end",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ marginRight: 1, mt: 1, cursor: 'pointer' }}
                  onClick={() => {
                    navigate("/reset-password");
                  }}>
                  Forgot Password?
                </Typography>

              </Box>
              <Box
                sx={{
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ height: "0px" }} />
              </Box>
              <Button
                fullWidth
                size="large"
                variant="contained"
                sx={{ marginBottom: 4 }}
                onClick={formSubmit}
              >
                Login
              </Button>
              <Box sx={{ textAlign: 'center', marginTop: '-1.5rem', marginBottom: '.5rem' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', marginTop: '-1rem', marginBottom: '.5rem' }}>
                  <hr style={{ flex: 1, border: '0', borderTop: '1px solid #ccc' }} />
                  <span style={{ margin: '0 1rem' }}>OR</span>
                  <hr style={{ flex: 1, border: '0', borderTop: '1px solid #ccc' }} />
                </Box>
              </Box>
              <Box sx={{ width: '100%', minWidth: '20rem', margin: 'auto' }}>
                <GoogleSignup btnText={'signin_with'} />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  justifyContent: "center",
                }}
              >
                <Typography variant="body2" sx={{ marginRight: 2 }}>
                  New on our platform?
                </Typography>
                <Typography
                  variant="body2"
                  onClick={() => {
                    navigate("/signup");
                  }}
                // sx={{ cursor: "pointer" }}
                >
                  <LinkStyled>Create an account</LinkStyled>
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <PositionedSnackbar
          severity={"info"}
          open={showMsg}
          message={msg}
          handleClose={handleClose}
        />
        <SimpleLoader
          open={isLoading}
          msg="Please wait while we are logging in..."
        />
      </Box>
      <Footer />
    </Box>
  );
};

export default LoginPage;
